import type { Character } from "./schema/character";
import type { CompiledChapter } from "./schema/compiled";
import type { Place } from "./schema/place";

export function mergeCharacters(chapters: readonly CompiledChapter[]): Character[] {
  const byId = new Map<string, Character>();
  for (const chapter of chapters) {
    for (const character of chapter.characters) {
      const prev = byId.get(character.id);
      byId.set(
        character.id,
        prev === undefined
          ? character
          : combineCharacter(prev, character, chapter.chapterId),
      );
    }
  }
  return [...byId.values()];
}

export function mergePlaces(chapters: readonly CompiledChapter[]): Place[] {
  const byId = new Map<string, Place>();
  for (const chapter of chapters) {
    for (const place of chapter.places) {
      if (!byId.has(place.id)) {
        byId.set(place.id, place);
      }
    }
  }
  return [...byId.values()];
}

function combineCharacter(prev: Character, next: Character, chapterId: string): Character {
  const looks = new Map(prev.appearances.map((look) => [look.id, look]));
  for (const look of next.appearances) {
    if (!looks.has(look.id)) {
      looks.set(look.id, look);
    }
  }
  const merged: Character = {
    ...prev,
    aliases: [...new Set([...prev.aliases, ...next.aliases])],
    appearances: [...looks.values()],
  };
  const currentName = prev.laterNames?.at(-1)?.displayName ?? prev.displayName;
  if (next.displayName !== currentName) {
    merged.laterNames = [
      ...(prev.laterNames ?? []),
      {
        chapterId,
        displayName: next.displayName,
        ...(next.namedInScene === undefined ? {} : { sceneId: next.namedInScene }),
      },
    ];
  }
  const imageUrl = prev.imageUrl ?? next.imageUrl;
  const markerColor = prev.markerColor ?? next.markerColor;
  const minor = prev.minor ?? next.minor;
  if (imageUrl !== undefined) {
    merged.imageUrl = imageUrl;
  }
  if (markerColor !== undefined) {
    merged.markerColor = markerColor;
  }
  if (minor !== undefined) {
    merged.minor = minor;
  }
  return merged;
}
